"use client";

import { useCallback, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { AssistantIf, ComposerPrimitive, useAui, useAuiState } from "@assistant-ui/react";
import { ArrowUpIcon, SquareIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { ContextBudgetDonut } from "@/components/assistant-ui/contextBudgetDonut";
import { handleCancel } from "@/components/assistant";
import { useDocAttachStore } from "../stores";
import { useThreadDocsContext } from "@/components/custom/contexts/threadContext";
import { ExtendedThinkingToggle, WebChip, McpServerChip } from "./ComposerToggles";
import { ComposerAddMenu } from "./ComposerAddMenu";
import { useMcpServers } from "@/components/custom/contexts/mcpServersContext";
import { useMcpServerDateFilters } from "@/components/custom/contexts/mcpServerDateFiltersContext";
import { buildConstraintAppendix } from "./buildConstraintAppendix";
import i18n from "@/lib/i18n";
import { useContextBudgetStore } from "@/components/custom/stores/contextBudgetStore";
import {
  ExtendContextModal,
  type ExtendContextChoice,
} from "@/components/custom/extendContextModal";
import { apiAuthFetch } from "@/lib/authFetch";
import { ClientLogger } from "@/lib/clientLogger";

export const ComposerAction: FC = () => {
  const { t } = useTranslation();
  const aui = useAui();
  const threadId = useAuiState(state => state.threadListItem.remoteId);
  const onCancel = useCallback(() => handleCancel(threadId), [threadId]);
  const docAttachInFlight = useDocAttachStore(s => s.inFlight);
  const docsLoading = useThreadDocsContext().selectedDocs === null;
  const sendBlocked = docAttachInFlight || docsLoading;
  const { selectedServers, allServers } = useMcpServers();
  const { dateFilters } = useMcpServerDateFilters();

  const snap = useContextBudgetStore(s => (threadId ? s.byThread[threadId] : undefined));
  const [modalOpen, setModalOpen] = useState(false);

  // Intercept fires once per thread: snap.trimmed flips true at >=75%, gets cleared once
  // the user has either acknowledged compaction or opted into extended mode. Provider
  // must also support the feature — otherwise we silently send (per plan decision #1).
  const shouldIntercept =
    !!threadId &&
    !!snap?.trimmed &&
    !snap.compactionPromptAcknowledged &&
    !!snap.extendedContextAvailable;

  const send = useCallback(() => {
    // CONSTRAINT injection: if any selected MCP server has an active date filter,
    // append a localized "# CONSTRAINT" block to the user's text BEFORE send.
    // We mutate the composer state so the appended text is visible to the user
    // (rendered in their message bubble) and reaches the LLM as part of the
    // user message — not as a hidden out-of-band channel.
    const composer = aui.composer();
    const base = composer.getState().text ?? "";
    const appendix = buildConstraintAppendix(
      selectedServers,
      allServers,
      dateFilters,
      i18n.language,
      t,
    );
    if (appendix) composer.setText(`${base}${appendix}`);
    composer.send();
  }, [aui, selectedServers, allServers, dateFilters, t]);

  const handleChoice = useCallback(
    async (choice: ExtendContextChoice) => {
      if (!threadId) return;
      const path =
        choice === "extend"
          ? `api/threads/${encodeURIComponent(threadId)}/extended-context`
          : `api/threads/${encodeURIComponent(threadId)}/acknowledge-compaction`;
      try {
        const res = await apiAuthFetch(path, { method: "POST" });
        if (!res.ok && res.status !== 204) {
          ClientLogger.warn(
            "extendContext.patch_non_ok",
            { metadata: { status: res.status, choice } },
          );
          // Fall through and send anyway — the snapshot will refresh on the next
          // turn and the user isn't blocked by a transient backend hiccup.
        } else {
          // Optimistically update the local snapshot so the modal doesn't re-fire
          // before the post-stream refresh lands. Mirrors what the backend just did.
          useContextBudgetStore.getState().set(threadId, {
            ...snap!,
            extendedContextEnabled: choice === "extend" ? true : snap!.extendedContextEnabled,
            compactionPromptAcknowledged: true,
            // When extending, the donut should switch to its neutral/teal look — backend
            // forces trimmed=false in extended mode. Mirror that locally.
            trimmed: choice === "extend" ? false : snap!.trimmed,
          });
        }
      } catch (err) {
        ClientLogger.warn("extendContext.patch_threw", { metadata: { err: String(err), choice } });
      } finally {
        setModalOpen(false);
        send();
      }
    },
    [threadId, send, snap],
  );

  const onSendClick = useCallback(() => {
    if (sendBlocked) return;
    if (shouldIntercept) {
      setModalOpen(true);
      return;
    }
    send();
  }, [sendBlocked, shouldIntercept, send]);

  const sendTooltip = sendBlocked
    ? docsLoading
      ? t("documents.loading")
      : t("documents.adding")
    : t("composer.send");

  return (
    <div className="aui-composer-action-wrapper relative mx-2 mb-2 flex flex-col gap-1">
      {snap?.extendedContextEnabled && (
        <div className="px-1 text-[11px] text-amber-500">
          {t("thread.extendedContextActive")}
        </div>
      )}

      <div className="flex items-end justify-between gap-2">
        <div className="flex flex-wrap-reverse items-center gap-x-2 gap-y-1 min-w-0">
          <ComposerAddMenu />
          <ExtendedThinkingToggle />
          <WebChip />
          {selectedServers.map(name => (
            <McpServerChip key={name} name={name} />
          ))}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <ContextBudgetDonut />

          <AssistantIf condition={({ thread }) => !thread.isRunning}>
            {/*
              First send-interception precedent in this codebase. We replace
              ComposerPrimitive.Send asChild with a plain button so we can gate
              the send behind the extend-context modal before assistant-ui's
              own submit handler runs. The button keeps type="button" — clicking
              it must NOT submit the surrounding form, since modal-cancel needs
              to leave the composer state untouched.
            */}
            <TooltipIconButton
              tooltip={sendTooltip}
              side="bottom"
              type="button"
              variant="default"
              size="icon"
              disabled={sendBlocked}
              onClick={onSendClick}
              className="aui-composer-send size-8 rounded-full"
              aria-label={t("composer.send")}
            >
              <ArrowUpIcon className="aui-composer-send-icon size-4" />
            </TooltipIconButton>
          </AssistantIf>

          <AssistantIf condition={({ thread }) => thread.isRunning}>
            <ComposerPrimitive.Cancel asChild onClick={onCancel}>
              <Button
                type="button"
                variant="default"
                size="icon"
                className="aui-composer-cancel size-8 rounded-full"
                aria-label={t("composer.stopGenerating")}
              >
                <SquareIcon className="aui-composer-cancel-icon size-3 fill-current" />
              </Button>
            </ComposerPrimitive.Cancel>
          </AssistantIf>
        </div>
      </div>

      <ExtendContextModal
        open={modalOpen}
        extendAvailable={!!snap?.extendedContextAvailable}
        onChoice={handleChoice}
        onCancel={() => setModalOpen(false)}
      />
    </div>
  );
};
